/**
 * AI视频 · 模板库 —— 2026-05-30 补。后端早有 video template(xb_video_template + /video/templates API + 5 seed),
 * 但前端页一直没建(老 VideoHomePage 的「浏览模板」是死链)。这里补上:浏览 → 点模板 → 带参进新建视频页。
 *
 * 2026-07-25 G1 铺量批:上卡墙规格。
 *  - 栅格从视口断点(`md:2 lg:3`)改**容器查询** `cardwall-cap`。实测原版在视口 780 那一段
 *    容器只有 476px 却已越过 `md`(768)→ 2 列 **230px**;而 1600 以上恒 3 列,卡宽一路撑到
 *    **416px**。两头都跌出 250~365 —— 因为量的是视口,而列宽取决于容器(= 视口 − 侧栏 − 内距)。
 *  - 圆角走形状锁单源 `CARD_WALL_RADIUS`(`<Card>` 默认 `rounded-lg` = `--radius × 1`,比卡墙其余卡都平)。
 *  - 页头换 `PageHeader`:该组件的注释写明它就是来收敛「三种各行其是的页头风」的,这页是漏网的一个。
 *  - 加载态从一行「加载中...」换成**按最终卡形状**的骨架屏 —— 骨架屏的意义是先告诉用户
 *    「这里会出现什么形状的东西」,一行字做不到。
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Film, Sparkles } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { PageHeader } from "@/shared/ui/PageHeader";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { cn } from "@/shared/lib/utils";
import {
  CARD_WALL,
  CARD_WALL_CAP,
  CARD_WALL_RADIUS,
  clickableCardProps,
} from "@/shared/ui/cardWall";
import { REAL_PERSON_TEMPLATE_TYPE, videoService } from "../api/videoService";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 封面位:★`coverUrl` 有值但**加载失败**时也要降级。原来只判 `coverUrl` 真假,
 * 404 的封面会直接把 `alt` 文本裸露在灰底上(测试环境实测:12 张卡全是「产品促销快剪 封面」)。
 * 海报模板库的 `TemplatePreview` 早就用 onError 兜住了,这里补齐同一套。
 */
function TemplateCover({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="aspect-video overflow-hidden bg-muted">
      {url && !failed ? (
        <img
          src={url}
          alt={`${name} 封面`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
          <Film className="h-8 w-8" />
          {url && <span className="text-2xs">封面加载失败</span>}
        </div>
      )}
    </div>
  );
}

export default function VideoTemplatesPage() {
  const navigate = useNavigate();
  // Phase C:取数从手搓 useState/useEffect 迁 react-query(统一缓存/加载/错误态,对齐列表页)。
  // 两态收敛批走查修:原来写死 type=PROMO,库里的 DRAMA 模板(品牌故事短剧)在这页
  // **永远不显示**(页面自称「模板库」却只给一半库存)。拉全量;类型信息卡面自带。
  const templatesQuery = useQuery({
    queryKey: ["video", "templates", "all"],
    queryFn: () => videoService.listTemplates(),
  });
  // 0924 车 D:REAL_PERSON 是真人出镜样片墙专用(只在 /ai-video/real 出现,「用这个拍」预填的是
  // 直发提示词,不是编排 brief)。D-api 已让不带 type 的列表排除它;这里再筛一遍防老 api
  // 回全量时混进来(点进去会落到通用创建页)。
  const templates =
    templatesQuery.data?.filter((t) => t.type !== REAL_PERSON_TEMPLATE_TYPE) ?? null;
  const error = templatesQuery.error ? errorText(templatesQuery.error) : null;

  const gridRef = useRef<HTMLDivElement>(null);
  useStaggerReveal(gridRef, { deps: [templates?.length ?? 0] });

  return (
    <div className="w-full space-y-6">
      <PageHeader
        backTo="/ai-video"
        icon={Film}
        title="视频舱 · 模板库"
        subtitle="挑个模板,自动带好脚本与参数,直接生成"
        actions={
          <Button variant="outline" onClick={() => navigate("/ai-video/create")}>
            <Sparkles className="mr-1.5 h-4 w-4" />
            自由新建
          </Button>
        }
      />

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className={CARD_WALL}>
        {templatesQuery.isLoading ? (
          <div className={CARD_WALL_CAP} data-testid="video-templates-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={cn("fx-skeleton border border-border/50 bg-card/40", CARD_WALL_RADIUS)}
                // 骨架高度 = 封面 16:9 + 文案区,按最终卡形状给,不用统一方块
                style={{ aspectRatio: "16 / 11.5" }}
              />
            ))}
          </div>
        ) : templates && templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              还没有视频模板
            </CardContent>
          </Card>
        ) : templates && templates.length > 0 ? (
          <div ref={gridRef} className={CARD_WALL_CAP}>
            {templates.map((t) => (
              <Card
                key={t.id}
                data-template-card={t.id}
                className={cn(
                  "cursor-pointer overflow-hidden transition hover:shadow-md",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  CARD_WALL_RADIUS
                )}
                // ★整卡可点必须同时给键盘:原来只有 onClick,Tab 永远到不了(门 H-D 实测)
                {...clickableCardProps(() => navigate(`/ai-video/create?templateId=${t.id}`))}
              >
                <TemplateCover url={t.coverUrl} name={t.name} />
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate font-medium">{t.name}</span>
                    <Badge variant="secondary" className="shrink-0 text-2xs">
                      {t.preferredRatio}
                    </Badge>
                  </div>
                  {t.description && (
                    <p className="line-clamp-2 text-xs text-muted-foreground">{t.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5 text-2xs text-muted-foreground">
                    <Badge variant="outline" className="text-2xs">
                      {t.preferredResolution}
                    </Badge>
                    <Badge variant="outline" className="text-2xs">
                      {t.preferredDurationSec}s
                    </Badge>
                    {t.preferredProvider && (
                      <Badge variant="outline" className="text-2xs font-mono">
                        {t.preferredProvider}
                      </Badge>
                    )}
                    {(t.industryNames ?? []).slice(0, 1).map((n) => (
                      <Badge key={n} variant="outline" className="text-2xs">
                        {n}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
